import { useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { useBusy } from '../../hooks/useBusy';
import { useConfirm } from '../../hooks/useConfirm';
import { useI18n, formatDay } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import StagePill from '../bout/StagePill';
import { CalendarIcon, BedIcon, ChartIcon, ClockIcon, SwapIcon, WarningIcon } from '../shared/Icons';
import type { BoutPlanView, BoutPlanItem, BoutPlanRingSummary, BoutPlanDaySummary, Ring } from '../../types';
import { successText } from '../../i18n/labels';
import { BADGE_XS } from '../shared/badgeSize';

interface Props {
  plan: BoutPlanView;
  rings: Ring[];
  tournamentId: string;
  /** When true the modal renders the same shape but hides every editable
   *  control (drag-reorder, ring select, Confirm-and-create, Discard).
   *  Set by the parent when `tournament.bout_plan_status === 'confirmed'`. */
  readOnly?: boolean;
  onClose: () => void;
  /** The write committed (#664): the parent closes the modal and reads the
   *  tournament and the brackets again. The read rejects when it fails. */
  onConfirmed: () => Promise<unknown>;
  onShowAnalysis: () => void;
  onPlanChange: (plan: BoutPlanView) => void;
}

export default function BoutPlanModal({ plan, rings, tournamentId, readOnly = false, onClose, onConfirmed, onShowAnalysis, onPlanChange }: Props) {
  const { showError } = useToast();
  const afterWrite = useAfterWrite();
  const { t } = useI18n();
  const { divisionLabel, boutCount } = useLabels();
  const pa = t.planAnalysis;
  const minRest = plan.min_rest_minutes;
  const [configuringBouts, setConfiguringBouts] = useState(false);
  // #605 — one write of the plan at a time: a move, a ring, the discard, the
  // confirm. A move and a ring change read the whole plan again, and an older
  // answer must not land after a newer change. The lock disables the trash,
  // Confirm and the ring selects. A click on a position does nothing until
  // the lock ends.
  const planBusy = useBusy();
  // #665 — each question comes BEFORE run(): a question inside run() holds
  // the lock while the person reads.
  const { confirm, askText } = useConfirm();

  async function confirmPlan() {
    if (!(await confirm({ message: t.tournament.confirmAndCreateConfirm, confirmLabel: t.tournament.confirmAndCreate }))) return;
    await planBusy.run(async () => {
      setConfiguringBouts(true);
      try {
        const { confirmBoutPlan } = await import('../../api/client');
        const res = await confirmBoutPlan(tournamentId);
        await afterWrite({ source: 'BoutPlanModal:confirm', success: successText(res.data), refresh: onConfirmed });
      } catch (err) { showError(err); }
      finally { setConfiguringBouts(false); }
    });
  }

  async function discardPlan() {
    if (!(await confirm({
      title: t.tournament.discardPlan, message: t.tournament.discardPlanConfirm, confirmLabel: t.common.delete, destructive: true,
    }))) return;
    await planBusy.run(async () => {
      try {
        const { discardBoutPlan } = await import('../../api/client');
        await discardBoutPlan(tournamentId);
        await afterWrite({ source: 'BoutPlanModal:discard', refresh: onConfirmed });
      } catch (err) { showError(err); }
    });
  }

  // #552 — the shared panel: the header and the footer stay in place, and the
  // 40 rows scroll between them. Before, "Confirm & Create Bouts" was at the
  // end of the scroll, stretched, and next to the destructive Delete.
  return (
    <ModalBackdrop onClose={onClose} maxWidth="5xl" layout="panel" ariaLabel={t.tournament.boutPreview}>
      <ModalHeader
        title={readOnly ? t.tournament.viewBoutPlan : t.tournament.boutPreview}
        subtitle={`${boutCount(plan.total_bouts)} · ~${plan.total_time}`}
      />

        <ModalBody className="space-y-4">
          {/* Summary table */}
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-3 py-2 text-left"></th>
                  {plan.rings.map((r: BoutPlanRingSummary) => (
                    <th key={r.ring_id} className="px-3 py-2 text-center">{r.ring_name}</th>
                  ))}
                  <th className="px-3 py-2 text-center font-bold">{t.common.total}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {plan.days.map((day: BoutPlanDaySummary) => {
                  const dayBouts = plan.bouts.filter((b: BoutPlanItem) => String(b.bout_date).slice(0, 10) === day.date);
                  return (
                    <tr key={day.date}>
                      <td className="px-3 py-2 font-medium text-xs">
                        {formatDay(day.date, 'EEE dd MMM')}
                        <div className="text-[10px] text-gray-400 font-normal">~{day.est_time}</div>
                      </td>
                      {plan.rings.map((r: BoutPlanRingSummary) => {
                        const ringDayBouts = dayBouts.filter((b: BoutPlanItem) => b.ring_id === r.ring_id);
                        const count = ringDayBouts.length;
                        const mins = ringDayBouts.reduce((s: number, b: BoutPlanItem) => s + (b.est_minutes || 0), 0);
                        return (
                          <td key={r.ring_id} className="px-3 py-2 text-center">
                            {count ? <>{count}<div className="text-[10px] text-gray-400">~{mins < 60 ? `${mins}m` : `${Math.floor(mins/60)}h ${mins%60}m`}</div></> : <span className="text-gray-300">—</span>}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-center font-bold">{dayBouts.length}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-bold text-xs">
                  <td className="px-3 py-2">{t.common.total}</td>
                  {plan.rings.map((r: BoutPlanRingSummary) => (
                    <td key={r.ring_id} className="px-3 py-2 text-center">
                      {r.bout_count}
                      <div className="text-[10px] text-gray-400 font-normal">~{r.est_time}</div>
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center text-base">
                    {plan.total_bouts}
                    <div className="text-[10px] text-gray-400 font-normal">~{plan.total_time}</div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Bout list grouped by day — with inline ring editing per bracket */}
          {plan.days.map((day: BoutPlanDaySummary) => {
            const dayBouts = plan.bouts.filter((b: BoutPlanItem) => String(b.bout_date).slice(0, 10) === day.date);
            if (!dayBouts.length) return null;
            return (
              <div key={day.date}>
                <div className="text-xs font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-sm mb-2 inline-flex items-center gap-1.5">
                  <CalendarIcon className="w-3.5 h-3.5" /> {formatDay(day.date, 'EEEE, dd MMM yyyy')} — {boutCount(dayBouts.length)}
                </div>
                <div className="space-y-1">
                  {dayBouts.map((b: BoutPlanItem) => {
                    // #480 — the rest comes from the server (lib/planAnalysis.ts): the
                    // start of this bout minus the end of each bout that feeds it,
                    // on the ring clocks. This window held its own estimate, which
                    // measured a rest across two rings from the day start.
                    const sameDay = b.rests.filter(r => r.rest_min !== null);
                    const isOvernight = b.rests.length > 0 && b.rests.every(r => r.other_day);
                    // No ring or no date on this bout or its source: not measured, never "overnight".
                    const unmeasured = b.rests.some(r => r.rest_min === null && !r.other_day);
                    const restMin = sameDay.length ? Math.min(...sameDay.map(r => r.rest_min as number)) : null;
                    const isCrossRing = sameDay.some(r => plan.bouts.find(o => o.position === r.source_position)?.ring_id !== b.ring_id);
                    return (
                    // #552 — on a phone a row has two lines: the pills, then the
                    // names and the ring. The three columns left 0 px for the names.
                    <div key={b.id} className="grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[1fr_auto_1fr] items-center gap-x-2 gap-y-1 py-1.5 px-2 rounded-sm hover:bg-gray-50 text-xs">
                      {/* Left: position + pills */}
                      <div className="flex flex-wrap md:flex-nowrap items-center gap-1.5 col-span-2 md:col-span-1">
                        {readOnly ? (
                          <span className="w-6 text-gray-400 text-right shrink-0">{b.position}</span>
                        ) : (
                          <span
                            className="w-6 text-gray-400 text-right shrink-0 cursor-pointer hover:text-gray-900 hover:font-bold"
                            data-testid={`plan-move-${b.position}`}
                            onClick={async () => {
                              // No question while a write of the plan runs: the click does nothing until the lock ends.
                              if (planBusy.busy) return;
                              const input = await askText({
                                message: t.tournament.moveBoutPrompt.replace('{n}', String(b.position)), initialValue: String(b.position),
                                confirmLabel: t.tournament.moveBout,
                              });
                              if (input === null) return;
                              const newPos = parseInt(input, 10);
                              if (isNaN(newPos) || newPos === b.position) return;
                              await planBusy.run(async () => {
                                let gp: typeof import('../../api/client').getBoutPlan;
                                try {
                                  const { reorderPlanBout, getBoutPlan } = await import('../../api/client');
                                  gp = getBoutPlan;
                                  await reorderPlanBout(tournamentId, b.id, newPos);
                                } catch (err) { showError(err); return; }
                                // #419 — the move committed. A failed read of the plan is a warning, not an error.
                                await afterWrite({ source: 'BoutPlanModal:move', refresh: async () => onPlanChange((await gp(tournamentId)).data) });
                              });
                            }}
                            title={t.modalLabels.moveBoutTip}
                          >{b.position}</span>
                        )}
                        <StagePill stage={b.stage} size="xs" className="shrink-0" />
                        {b.gender && <span className={`badge badge-gray ${BADGE_XS}`}>{divisionLabel(b.gender)}</span>}
                        <span className={`badge badge-gray ${BADGE_XS}`}>{b.age_group}</span>
                        <span className={`badge badge-gray ${BADGE_XS}`}>{b.weight_class}</span>
                        <span className="text-[10px] text-gray-400 shrink-0 inline-flex items-center gap-0.5"><ClockIcon className="w-2.5 h-2.5" /> {b.est_minutes}m</span>
                        {isOvernight && (
                          <span className="text-[10px] text-green-600 shrink-0 inline-flex items-center gap-0.5"><BedIcon className="w-3 h-3" /> {pa.overnight}</span>
                        )}
                        {unmeasured && (
                          <span className="text-[10px] text-gray-400 shrink-0 inline-flex items-center gap-0.5" title={pa.restUnknownTitle}><BedIcon className="w-3 h-3" /> {pa.restUnknown}</span>
                        )}
                        {restMin !== null && (
                          <span data-testid={`plan-rest-${b.position}`} className={`text-[10px] shrink-0 inline-flex items-center gap-0.5 ${restMin >= minRest ? 'text-green-600' : restMin >= minRest / 2 ? 'text-amber-600' : 'text-red-600'}`}>
                            <BedIcon className="w-3 h-3" /> {restMin <= 0 ? pa.noRest : pa.restMinutes.replace('{n}', String(restMin))}{isCrossRing && <SwapIcon className="w-3 h-3" />}
                          </span>
                        )}
                        {restMin !== null && restMin < minRest && (
                          <span className="text-[10px] font-semibold shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-300"
                                title={pa.breakTitle.replace('{min}', String(minRest))}>
                            <WarningIcon className="w-3.5 h-3.5 inline-block align-text-bottom" /> {t.tournament.breakNeeded}
                          </span>
                        )}
                        {!b.ring_fits && (
                          <span className="text-amber-600 shrink-0 inline-flex" title={pa.ringNotFit}><WarningIcon className="w-3 h-3" /></span>
                        )}
                      </div>
                      {/* Center: names anchored on vs */}
                      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1 md:mx-2 min-w-0">
                        <span data-testid="plan-row-red" className="text-red-600 truncate text-right">{b.red_name}</span>
                        <span className="text-gray-400 shrink-0">vs</span>
                        <span className="text-blue-600 truncate">{b.blue_name}</span>
                      </div>
                      {/* Right: ring (selector or static label) */}
                      <div className="flex justify-end">
                        {readOnly ? (
                          <span className="text-[10px] px-1 py-0.5 text-gray-500 shrink-0">
                            {rings.find((r: Ring) => r.id === b.ring_id)?.name ?? b.ring_name ?? '—'}
                          </span>
                        ) : (
                          <select
                            aria-label={t.common.ring}
                            className="text-xs border border-gray-200 rounded-md px-1.5 py-1 bg-white shrink-0"
                            value={b.ring_id}
                            disabled={planBusy.busy}
                            onChange={(e) => {
                              const newRingId = e.target.value;
                              if (newRingId === b.ring_id) return;
                              void planBusy.run(async () => {
                                // Optimistic update
                                onPlanChange({
                                  ...plan,
                                  bouts: plan.bouts.map((bout: BoutPlanItem) => bout.id === b.id ? { ...bout, ring_id: newRingId } : bout)
                                });
                                let gp: typeof import('../../api/client').getBoutPlan;
                                try {
                                  const { updatePlanBoutRing, getBoutPlan } = await import('../../api/client');
                                  gp = getBoutPlan;
                                  await updatePlanBoutRing(tournamentId, b.id, newRingId);
                                } catch (err) {
                                  // #605 — a refusal restores the plan from before the change.
                                  // The lock allows no other change of the plan.
                                  onPlanChange(plan);
                                  showError(err);
                                  return;
                                }
                                // #419 — the ring committed. A failed read of the plan is a warning, not an error.
                                await afterWrite({ source: 'BoutPlanModal:ring', refresh: async () => onPlanChange((await gp(tournamentId)).data) });
                              });
                            }}>
                            {rings.map((r: Ring) => <option key={r.id} value={r.id}>{r.name}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

        </ModalBody>

      {/* Confirm and Discard plan are hidden when readOnly
          (post-confirm view). The first focus goes to Analysis: the first
          field is the ring select of bout 1, and an arrow key there writes. */}
      <ModalFooter
        actions={readOnly ? undefined : [{ label: t.tournament.discardPlan, onClick: discardPlan, destructive: true, disabled: planBusy.busy, testId: 'plan-discard' }]}
        secondary={(
          <button type="button" data-autofocus onClick={onShowAnalysis} className="btn-outline btn-sm">
            <ChartIcon className="w-3.5 h-3.5" /> {pa.open}
          </button>
        )}
        onSave={readOnly ? undefined : confirmPlan}
        busy={configuringBouts}
        busyLabel={t.tournament.creatingBouts}
        saveLabel={t.tournament.confirmAndCreate}
        saveDisabled={planBusy.busy}
        onCancel={onClose}
        cancelLabel={t.common.close}
      />
    </ModalBackdrop>
  );
}
